// Pre-emptive form registration for ft.js.
//
// Goal: every form on a customer's page is registered with the server
// the moment ft.js loads — *before* anyone submits — so the customer's
// form registry on /install is populated immediately. The server can
// then auto-flag noise patterns (admin-ajax, comment forms, wc-ajax)
// as `pending_review`, and ft.js can client-side-suppress submissions
// to forms the customer has explicitly marked `excluded`.
//
// Cache: localStorage keyed by site_key, 5 minute TTL. We never re-fetch
// inside the TTL window even if new forms appear — the MutationObserver
// adds newly-detected forms to a pending batch and we flush them with a
// short debounce so a single page never makes more than a couple of
// /api/forms/register calls.
//
// Side-effect-free pure helpers (formIdentifierFor, isExcluded) are
// directly testable; the I/O wrapper (createRegistry) is the only piece
// that touches network/storage.

const CACHE_TTL_MS = 5 * 60 * 1000;

// Mirrors the identifier ft.js's capture() builds for the form_identifier
// field: action attribute first, then id, then name. Pure for the test suite.
//
// 2a: identity is read via getAttribute — the form.id / form.name DOM
// properties are shadowed by named inputs (<input name="id">, <input
// name="name">), which would otherwise yield "[object HTMLInputElement]".
// 2b: no path fallback — a form with no real identifier returns "" so it is
// never registered/keyed on the URL path; the server records such a form as
// pending_review (unidentified) without dropping any lead.
export function formIdentifierFor(form) {
  if (!form || form.tagName !== "FORM" || typeof form.getAttribute !== "function") {
    return "";
  }
  return (
    form.getAttribute("action") ||
    form.getAttribute("id") ||
    form.getAttribute("name") ||
    ""
  );
}

// QA16 W-FTJS: keyed by BOTH apiBase and siteKey, not siteKey alone. Two
// embeds sharing one site_key but pointed at different backends (the general
// multi-instance capability's whole point — e.g. a staging+prod pair that
// happens to reuse a site_key) would otherwise share ONE form-exclusion
// cache, so one backend's excluded/pending_review flags for a form would
// incorrectly suppress or allow capture for the OTHER backend's registry of
// that same form.
function cacheKey(apiBase, siteKey) {
  return "ft_forms_" + apiBase + "_" + siteKey;
}

function readCache(apiBase, siteKey) {
  try {
    var raw = localStorage.getItem(cacheKey(apiBase, siteKey));
    if (!raw) return null;
    var parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (typeof parsed.ts !== "number") return null;
    if (Date.now() - parsed.ts > CACHE_TTL_MS) return null;
    return parsed.forms || {};
  } catch (e) {
    return null;
  }
}

function writeCache(apiBase, siteKey, forms) {
  try {
    localStorage.setItem(
      cacheKey(apiBase, siteKey),
      JSON.stringify({ ts: Date.now(), forms: forms })
    );
  } catch (e) {
    // localStorage disabled / quota exceeded — registry still works,
    // it just costs an extra round trip on the next pageview.
  }
}

// Factory that closes over site_key + API base + send. Returns:
//   register(formIdentifiers) — POST to /api/forms/register, merge result
//   isExcluded(formIdentifier) — synchronous lookup in the cached map
export function createRegistry(opts) {
  var siteKey = opts.siteKey;
  var apiBase = opts.apiBase;
  var pageUrl = opts.pageUrl;
  var fetchImpl = opts.fetchImpl || (typeof fetch === "function" ? fetch : null);

  var cached = readCache(apiBase, siteKey) || {};
  var pending = []; // identifiers queued during a flight
  var inflight = false;
  var debounceTimer = null;

  function isExcluded(formIdentifier) {
    return cached[formIdentifier] === "excluded";
  }

  function flush() {
    debounceTimer = null;
    if (inflight || !fetchImpl) return;
    // Only register identifiers we haven't seen yet. Server is idempotent
    // but we save bandwidth + CPU by skipping known ones.
    var toSend = [];
    for (var i = 0; i < pending.length; i++) {
      var id = pending[i];
      if (cached[id] == null) toSend.push({ form_identifier: id });
    }
    pending.length = 0;
    if (toSend.length === 0) return;
    inflight = true;
    try {
      fetchImpl(apiBase + "/api/forms/register", {
        method: "POST",
        credentials: "include",
        mode: "cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          site_key: siteKey,
          page_url: pageUrl,
          forms: toSend,
        }),
      })
        .then(function (r) {
          return r && r.ok ? r.json() : null;
        })
        .then(function (data) {
          inflight = false;
          if (!data || !data.forms) return;
          for (var i = 0; i < data.forms.length; i++) {
            var f = data.forms[i];
            if (f && f.form_identifier && f.tracking_status) {
              cached[f.form_identifier] = f.tracking_status;
            }
          }
          writeCache(apiBase, siteKey, cached);
          // If new identifiers arrived during the flight, flush them now.
          if (pending.length > 0) flush();
        })
        ["catch"](function () {
          inflight = false;
        });
    } catch (e) {
      inflight = false;
    }
  }

  function register(formIdentifiers) {
    if (!formIdentifiers || formIdentifiers.length === 0) return;
    for (var i = 0; i < formIdentifiers.length; i++) {
      var id = formIdentifiers[i];
      if (!id) continue;
      if (cached[id] != null) continue; // already known
      if (pending.indexOf(id) !== -1) continue;
      pending.push(id);
    }
    if (pending.length === 0) return;
    if (debounceTimer == null) {
      // Tiny debounce: lets the initial batch + a flurry of MutationObserver
      // adds collapse into a single request.
      debounceTimer = setTimeout(flush, 50);
    }
  }

  return {
    register: register,
    isExcluded: isExcluded,
  };
}
